import { useState } from "react";
import { ArrowLeftRight, Reply, TrendingUp } from "lucide-react";
import { ExpenseCategory, IncomingKind, dayjs } from "@boo/shared";
import type {
  ClassifyIncomingRequest,
  IncomeCategory,
  PaybackCandidate,
  Transaction,
} from "@boo/shared";
import { fetchPaybackCandidates } from "../../../../services/expenseApi";
import { useRecurringIncomeStore } from "../../store/useRecurringIncomeStore";
import {
  CATEGORY_LABELS,
  EXPENSE_CATEGORIES,
  INCOME_CATEGORY_LABELS,
} from "../../types";

const Mode = {
  Payback: "payback",
  Income: "income",
} as const;
type Mode = (typeof Mode)[keyof typeof Mode];

interface IncomingClassifierProps {
  transaction: Transaction;
  onClassify: (id: string, payload: ClassifyIncomingRequest) => Promise<void>;
}

const KIND_BUTTON_CLASS =
  "inline-flex items-center gap-1.5 rounded-[8px] border-[0.5px] px-2.5 py-1.5 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-40";
const OPTION_CLASS =
  "w-full flex items-center justify-between gap-3 rounded-[8px] border-[0.5px] border-border bg-card px-2.5 py-1.5 text-left text-[12px] text-foreground transition-colors cursor-pointer hover:bg-[var(--surface-muted)] disabled:opacity-40";

function kindClass(active: boolean) {
  return `${KIND_BUTTON_CLASS} ${
    active
      ? "border-foreground bg-foreground text-[var(--page-bg)]"
      : "border-border bg-[var(--surface-muted)] text-muted-foreground hover:text-foreground"
  }`;
}

// Says what money received is: a payback of something the user fronted, real
// income, or a transfer between their own accounts.
export default function IncomingClassifier({
  transaction,
  onClassify,
}: IncomingClassifierProps) {
  const recurringIncomes = useRecurringIncomeStore((s) => s.items);
  const [mode, setMode] = useState<Mode | null>(null);
  const [candidates, setCandidates] = useState<PaybackCandidate[] | null>(null);
  const [offsetCategory, setOffsetCategory] = useState<ExpenseCategory>(
    ExpenseCategory.FOOD,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function classify(payload: ClassifyIncomingRequest) {
    setBusy(true);
    setError(null);
    try {
      await onClassify(transaction.id, payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
      setBusy(false);
    }
  }

  async function openPayback() {
    setMode(Mode.Payback);
    setError(null);
    if (candidates) return;
    try {
      setCandidates(await fetchPaybackCandidates(transaction.id));
    } catch {
      // The category fallback below still works without suggestions.
      setCandidates([]);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          disabled={busy}
          onClick={openPayback}
          className={kindClass(mode === Mode.Payback)}
        >
          <Reply className="w-3.5 h-3.5" />
          Payback
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setMode(Mode.Income)}
          className={kindClass(mode === Mode.Income)}
        >
          <TrendingUp className="w-3.5 h-3.5" />
          Income
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => classify({ kind: IncomingKind.Transfer })}
          className={kindClass(false)}
        >
          <ArrowLeftRight className="w-3.5 h-3.5" />
          Transfer
        </button>
      </div>

      {mode === Mode.Payback && (
        <div className="flex flex-col gap-1.5">
          <div className="text-[11px] text-muted-foreground">
            {candidates === null
              ? "Looking for the expense this repays…"
              : candidates.length > 0
                ? "Which expense does this repay?"
                : "No expense from the week before matches."}
          </div>
          {candidates?.map((c) => (
            <button
              key={c.expenseId}
              type="button"
              disabled={busy}
              onClick={() =>
                classify({ kind: IncomingKind.Payback, expenseId: c.expenseId })
              }
              className={OPTION_CLASS}
            >
              <span className="min-w-0 truncate">
                {c.title}
                <span className="text-muted-foreground">
                  {" "}
                  · {dayjs(c.date).format("D MMM")}
                  {c.memberName ? ` · settles ${c.memberName}` : ""}
                </span>
              </span>
              <span className="shrink-0 [font-variant-numeric:tabular-nums]">
                ฿{c.amount.toLocaleString("en-US")}
              </span>
            </button>
          ))}
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            <span>Not in the app — take it off</span>
            <select
              value={offsetCategory}
              disabled={busy}
              onChange={(e) =>
                setOffsetCategory(e.target.value as ExpenseCategory)
              }
              className="rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-2 py-1.5 text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-border"
              aria-label="Category to offset"
            >
              {EXPENSE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {CATEGORY_LABELS[cat]}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                classify({
                  kind: IncomingKind.Payback,
                  category: offsetCategory,
                })
              }
              className={kindClass(false)}
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {mode === Mode.Income && (
        <div className="flex flex-col gap-1.5">
          {recurringIncomes.length > 0 && (
            <>
              <div className="text-[11px] text-muted-foreground">
                Part of a recurring income? Future deposits from this sender
                will follow.
              </div>
              {recurringIncomes.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    classify({
                      kind: IncomingKind.Income,
                      recurringIncomeId: item.id,
                    })
                  }
                  className={OPTION_CLASS}
                >
                  <span className="min-w-0 truncate">{item.title}</span>
                  <span className="shrink-0 text-muted-foreground [font-variant-numeric:tabular-nums]">
                    ฿{item.amount.toLocaleString("en-US")}
                  </span>
                </button>
              ))}
            </>
          )}
          <div className="text-[11px] text-muted-foreground">
            One-off income:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(INCOME_CATEGORY_LABELS) as IncomeCategory[]).map(
              (cat) => (
                <button
                  key={cat}
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    classify({ kind: IncomingKind.Income, incomeCategory: cat })
                  }
                  className={kindClass(false)}
                >
                  {INCOME_CATEGORY_LABELS[cat]}
                </button>
              ),
            )}
          </div>
        </div>
      )}

      {error && <p className="text-[12px] text-red-500 m-0">{error}</p>}
    </div>
  );
}
